import { Component, EventEmitter, Input, Output } from '@angular/core';

/** Chip-szín a prototípus szerint: positive/pending/error/primary/neutral. */
export type SzChipTone = 'positive' | 'pending' | 'error' | 'primary' | 'neutral';

/**
 * Enum-érték → chip-szín a kulcs neve alapján (ClaimState, MemberState, RedemptionState, SubstituteProfileState…):
 * függő/új → sárga, jóváhagyott/lezárt/kiadott/aktív → zöld, elutasított/letiltott/visszavont → piros, egyéb → semleges.
 */
export function szEnumTone(enumObj: any, value: any): SzChipTone {
  const key = enumObj && value !== null && value !== undefined ? String(enumObj[value] ?? value) : '';
  if (/Pending|New|Requested|UnderInspection|Draft|Reserved/i.test(key)) return 'pending';
  if (/Approved|Booked|Issued|Active|Credited|Completed|Verified|Success|Passed/i.test(key)) return 'positive';
  if (/^Closed$/i.test(key)) return 'neutral';
  if (/Rejected|Blocked|Cancelled|Expired|Failed|Deleted|Revoked/i.test(key)) return 'error';
  if (/Planned|Upcoming|Info/i.test(key)) return 'primary';
  return 'neutral';
}

/**
 * Státusz-chip: 24 px, 12 px lekerekítés, tint háttér, kontrasztos szöveg.
 * <sz-chip tone="positive">Aktív</sz-chip>
 */
@Component({
  standalone: false,
  selector: 'sz-chip',
  template: `<span class="sz-chip" [ngClass]="'sz-chip-' + tone" [class.sz-chip-mono]="mono"><span *ngIf="icon" class="sz-icon sz-icon-16">{{ icon }}</span><ng-content></ng-content></span>`,
})
export class SzChipComponent {
  @Input() tone: SzChipTone = 'neutral';
  @Input() icon?: string;
  @Input() mono = false;
}

/**
 * KPI-kártya a fejlécekhez: címke, érték, opcionális alsor és szín.
 * <sz-kpi label="Hitelkeret" value="15 000 000 Ft" sub="…" tone="primary"></sz-kpi>
 */
@Component({
  standalone: false,
  selector: 'sz-kpi',
  template: `
    <div class="sz-kpi" [ngClass]="'sz-kpi-' + tone" [class.sz-kpi-clickable]="clickable" (click)="clickable && pick.emit()">
      <div class="sz-kpi-label">{{ label }}</div>
      <div class="sz-kpi-value" [class.sz-mono]="mono">{{ value }}</div>
      <div class="sz-kpi-sub" *ngIf="sub">{{ sub }}</div>
      <div class="sz-kpi-bar" *ngIf="progress !== undefined && progress !== null">
        <div class="sz-kpi-bar-fill" [style.width.%]="progress"></div>
      </div>
    </div>
  `,
})
export class SzKpiComponent {
  @Input() label = '';
  @Input() value: string | number = '';
  @Input() sub?: string;
  @Input() tone: 'default' | 'primary' | 'positive' | 'pending' | 'error' | 'inverse' = 'default';
  @Input() mono = true;
  @Input() progress?: number | null;
  @Input() clickable = false;
  @Output() pick = new EventEmitter<void>();
}

/**
 * Oldalfejléc: cím, alcím, jobbra műveletek (ng-content).
 */
@Component({
  standalone: false,
  selector: 'sz-page-header',
  template: `
    <div class="sz-page-header">
      <div class="sz-page-header-text">
        <div class="sz-page-header-title">{{ title }}</div>
        <div class="sz-page-header-sub" *ngIf="subtitle">{{ subtitle }}</div>
      </div>
      <div class="sz-page-header-actions"><ng-content></ng-content></div>
    </div>
  `,
})
export class SzPageHeaderComponent {
  @Input() title = '';
  @Input() subtitle?: string;
}

export interface SzPillOption<T = any> {
  label: string;
  value: T;
  count?: number;
  /** Nem választható opció (halványan látszik); az okát a title mutatja rámutatáskor. */
  disabled?: boolean;
  title?: string;
}

/**
 * Szegmens / pill-szűrő: <sz-pills [options]="…" [(value)]="state"></sz-pills>
 */
@Component({
  standalone: false,
  selector: 'sz-pills',
  template: `
    <div class="sz-pills" [class.sz-pills-card]="card">
      <button
        *ngFor="let o of options"
        type="button"
        class="sz-pill"
        [class.sz-pill-active]="o.value === value"
        [disabled]="!!o.disabled"
        [style.opacity]="o.disabled ? 0.45 : null"
        [style.cursor]="o.disabled ? 'not-allowed' : null"
        [attr.title]="o.title || null"
        (click)="!o.disabled && select(o.value)"
      >
        {{ o.label }}<span *ngIf="o.count !== undefined && o.count !== null" class="sz-pill-count">{{ o.count }}</span>
      </button>
    </div>
  `,
})
export class SzPillsComponent<T = any> {
  /** Kártya-stílus: fehér háttér vonallal (lista-fejlécekben), a szürke sáv helyett. */
  @Input() card = false;
  @Input() options: SzPillOption<T>[] = [];
  @Input() value: T;
  @Output() valueChange = new EventEmitter<T>();

  select(v: T) {
    if (v !== this.value) {
      this.value = v;
      this.valueChange.emit(v);
    }
  }
}

/**
 * Információs sáv: info / warn / error / ok, opcionális ikon és művelet-gomb.
 */
@Component({
  standalone: false,
  selector: 'sz-banner',
  template: `
    <div class="sz-banner" [ngClass]="'sz-banner-' + kind">
      <span class="sz-icon" *ngIf="icon">{{ icon }}</span>
      <div class="sz-banner-text"><ng-content></ng-content></div>
      <button *ngIf="action" type="button" class="sz-banner-action" (click)="act.emit()">{{ action }}</button>
    </div>
  `,
})
export class SzBannerComponent {
  @Input() kind: 'info' | 'warn' | 'error' | 'ok' = 'info';
  @Input() icon?: string;
  @Input() action?: string;
  @Output() act = new EventEmitter<void>();
}

export interface SzTimelineStep {
  label: string;
  sub?: string;
  state: 'done' | 'current' | 'todo' | 'error';
}

/**
 * Állapot-idővonal (reklamáció, beváltás): lépések pöttyel és vonallal.
 */
@Component({
  standalone: false,
  selector: 'sz-timeline',
  template: `
    <div class="sz-timeline" [class.sz-timeline-horizontal]="horizontal">
      <div *ngFor="let s of steps; let last = last" class="sz-timeline-step" [ngClass]="'sz-timeline-' + s.state">
        <div class="sz-timeline-dot"><span class="sz-icon sz-icon-16" *ngIf="s.state === 'done'">check</span><span class="sz-icon sz-icon-16" *ngIf="s.state === 'error'">close</span></div>
        <div class="sz-timeline-line" *ngIf="!last"></div>
        <div class="sz-timeline-text">
          <div class="sz-timeline-label">{{ s.label }}</div>
          <div class="sz-timeline-sub" *ngIf="s.sub">{{ s.sub }}</div>
        </div>
      </div>
    </div>
  `,
})
export class SzTimelineComponent {
  @Input() steps: SzTimelineStep[] = [];
  @Input() horizontal = false;
}

/**
 * Üres állapot: ikon, cím, szöveg, opcionális művelet.
 */
@Component({
  standalone: false,
  selector: 'sz-empty',
  template: `
    <div class="sz-empty">
      <span class="sz-icon sz-icon-24 sz-empty-icon">{{ icon }}</span>
      <div class="sz-empty-title">{{ title }}</div>
      <div class="sz-empty-text" *ngIf="text">{{ text }}</div>
      <button *ngIf="action" type="button" class="sz-btn sz-btn-outline" (click)="act.emit()">{{ action }}</button>
    </div>
  `,
})
export class SzEmptyComponent {
  @Input() icon = 'inbox';
  @Input() title = 'Nincs találat';
  @Input() text?: string;
  @Input() action?: string;
  @Output() act = new EventEmitter<void>();
}

export const SZ_UI_COMPONENTS = [
  SzChipComponent,
  SzKpiComponent,
  SzPageHeaderComponent,
  SzPillsComponent,
  SzBannerComponent,
  SzTimelineComponent,
  SzEmptyComponent,
];
